import { BridgeCallHandlers, BridgeClientShareReportKeys, BridgeClientShareTypes } from "@/interfaces/enums";
import bridge from "@/utils/dsBridge";
import html2canvas, { Options } from "html2canvas";
import { useEffect, useRef, useState } from "react";
import { createRoot, Root } from "react-dom/client";
import Loading from "@/containers/activities/LoadingPoster";
import { canUseDom } from "@/utils/components-utils";
import compressorFile from "@/utils/compressorFile";

interface ShareTypes {
	linkUrl?: string;
	shareCode?: string;
	subtitle?: string;
	shareScene?: BridgeClientShareReportKeys;
	shareType?: BridgeClientShareTypes;
	description?: string;
	xhsTitle?: string;
	xhsContent?: string;
}

function waitNextFrame() {
	return new Promise<void>((resolve) => {
		requestAnimationFrame(() => resolve());
	});
}

export default function usePosterShare() {
	const [loading, setLoading] = useState(false);
	const processRef = useRef<boolean>(true);
	const loadingRef = useRef(false);
	const divRef = useRef<HTMLDivElement | null>(null);
	const rootRef = useRef<Root | null>(null);

	const onShare = async (
		element: HTMLElement,
		option: ShareTypes,
		cacheUrl?: string,
		setCacheUrl?: (url: string) => void,
	) => {
		if (loadingRef.current) return;

		try {
			// 从缓存中获取 -- 各个业务自己实现缓存
			if (cacheUrl) {
				handleBridgeShare(cacheUrl, option);
				return true;
			}

			// 同步打开 loading；再让出一帧给 UI 绘制，避免 iOS 18 与 canvas 抢资源
			loadingRef.current = true;
			processRef.current = true;
			setLoading(true);
			await waitNextFrame();

			if (!processRef.current) return;

			const dataUrl = await canvas2ImgDataUrl(element);

			if (!processRef.current) return;

			handleBridgeShare(dataUrl, option);
			if (setCacheUrl) {
				setCacheUrl(dataUrl);
			}
			return true;
		} catch (error) {
			throw error;
		} finally {
			loadingRef.current = false;
			setLoading(false);
		}
	};

	const handleBridgeShare = (imgUrl: string, params: ShareTypes) => {
		if (!processRef.current) return;

		bridge?.call(BridgeCallHandlers.ON_SHARE_BUTTON_CLICK, {
			shareTitle: params.subtitle + "\n" + params.linkUrl,
			xhsTitle: params?.xhsTitle,
			xhsContent: params?.xhsContent,
			shareUrl: params.linkUrl,
			shareCode: params.shareCode,
			shareImgUrl: imgUrl,
			shareType: params.shareType || BridgeClientShareTypes.IMAGE,
			shareScene: params.shareScene,
		});
	};

	useEffect(() => {
		if (!canUseDom) return;
		if (loading) {
			const div = document.createElement("div");
			divRef.current = div;
			document.body.appendChild(div);
			const root = createRoot(div);
			rootRef.current = root;
			root.render(<Loading onClick={handleCancel} />);
		} else {
			if (rootRef.current) {
				rootRef.current.unmount();
				rootRef.current = null;
			}
			if (divRef.current?.parentNode) {
				divRef.current.parentNode.removeChild(divRef.current);
				divRef.current = null;
			}
		}
	}, [loading]);

	const handleCancel = () => {
		processRef.current = false;
		loadingRef.current = false;
		setLoading(false);
	};

	return onShare;
}

export async function canvas2ImgDataUrl(element: HTMLElement, options?: Partial<Options>) {
	try {
		const canvas = await html2canvas(
			element,
			options || {
				scale: window.devicePixelRatio || 2,
				useCORS: true,
				backgroundColor: null,
			},
		);
		const dataUrl = await compressorDataUrl(canvas);
		return dataUrl;
	} catch (error) {
		throw error;
	}
}

function compressorDataUrl(_canvas: HTMLCanvasElement): Promise<string> {
	return new Promise((resolve, reject) => {
		_canvas.toBlob(async (blob) => {
			try {
				if (!blob) {
					reject(new Error("canvas to blob error"));
					return;
				}
				const _blob = await compressorFile(blob, {
					maxWidth: 1024,
					quality: 0.8,
				});
				const dataUrl = await blobToBase64(_blob);
				resolve(dataUrl);
			} catch (error) {
				reject(error);
			}
		}, "image/jpeg");
	});
}

function blobToBase64(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = function () {
			resolve(reader.result as any);
		};
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});
}
